import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_INVENTORY_URL } from '@/utils';
import { AssetApi, AssetsFilter } from '../models';

const assetsService = createApi({
  reducerPath: 'assetsService',
  baseQuery: createBaseQueryWithReauth(`${API_INVENTORY_URL}/assets`),
  tagTypes: ['Assets'],
  endpoints: (builder) => ({
    getAssets: builder.query<
      PaginatedResponse<AssetApi>,
      { filters?: AssetsFilter; paginated: PaginatedRequest; search?: string }
    >({
      query: ({ filters, paginated, search }) => {
        const params = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          excludeStatus: 'disabled',
          ...(filters?.typeId &&
            typeof filters.typeId === 'string' &&
            filters.typeId.trim() !== '' && { typeId: filters.typeId }),
          ...(filters?.statuses &&
            filters.statuses.length > 0 && {
              status: filters.statuses.join(','),
            }),
          ...(filters?.cityId &&
            filters.cityId.length > 0 && {
              cityId: filters.cityId.join(','),
            }),
          ...(filters?.userId &&
            typeof filters.userId === 'string' &&
            filters.userId.trim() !== '' && { userId: filters.userId }),
          ...(filters?.brandId &&
            typeof filters.brandId === 'string' &&
            filters.brandId.trim() !== '' && { brandId: filters.brandId }),
          ...(filters?.modelId &&
            typeof filters.modelId === 'string' &&
            filters.modelId.trim() !== '' && { modelId: filters.modelId }),
          ...(filters?.officeId &&
            typeof filters.officeId === 'string' &&
            filters.officeId.trim() !== '' && { officeId: filters.officeId }),
          ...(filters?.barcode &&
            typeof filters.barcode === 'string' &&
            filters.barcode.trim() !== '' && { barcode: filters.barcode.trim() }),
          ...(search && search.trim() !== '' && { search: search.trim() }),
        };
        return {
          url: '',
          params: {
            ...params,
            populate: 'assetType,brand,model,location,office,office.city,user',
          },
        };
      },
      providesTags: ['Assets'],
    }),
    createAsset: builder.mutation({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
        params: {
          populate: 'assetType,brand,model,location,office,office.city,user',
        },
      }),
      invalidatesTags: ['Assets'],
    }),
    getAssetById: builder.query<AssetApi, { assetId: string }>({
      query: ({ assetId }) => ({
        url: `/${assetId}`,
        params: {
          populate: 'assetType,brand,model,location,office,office.city,user',
        },
      }),
      providesTags: ['Assets'],
    }),
    updateAsset: builder.mutation<
      AssetApi,
      { assetId: string; body: Partial<AssetApi> }
    >({
      query: ({ assetId, body }) => ({
        url: `/${assetId}`,
        method: 'PATCH',
        body,
        params: {
          populate: 'assetType,brand,model,location,office,office.city,user',
        },
      }),
      invalidatesTags: ['Assets'],
    }),
  }),
});

export default assetsService;
export const {
  useGetAssetsQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useGetAssetByIdQuery,
} = assetsService;
